// src/landing/components/NavBar.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Menu, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LanguageContext } from '../../common/contexts/LanguageContext';
import '../../landing/styles/NavBar.css';

import { db } from '../../common/firebase';
import { doc, getDoc } from 'firebase/firestore/lite';

export default function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();

  // 메뉴 상태
  const [active, setActive] = useState('service');
  const [visibleSections, setVisibleSections] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // 언어 Context
  const { lang, setLang, t } = useContext(LanguageContext);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef();

  // 외부 클릭 시 언어 드롭다운 닫기
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  // Firestore에서 표시할 섹션 설정 불러오기
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'settings'));
        const cfg = snap.exists() ? snap.data() : {};
        setVisibleSections(cfg.visibleSections || ['service','how','about','gallery','contact']);
      } catch {
        setVisibleSections(['service','how','about','gallery','contact']);
      }
    })();
  }, []);

  // 섹션 이동
  const goToSection = (id) => {
    navigate({ pathname: '/', hash: `#${id}` });
    setActive(id);
    setIsOpen(false);
    // 스크롤 보장
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  // 로고 클릭: 홈으로 이동하거나, 이미 홈이면 맨 위로 스크롤
  const handleLogoClick = () => {
    setIsOpen(false);
    setActive('service');
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const allMenus = [
    { id: 'service' },
    { id: 'how'     },
    { id: 'about'   },
    { id: 'gallery' },
    { id: 'contact' },
  ];
  const menus = allMenus.filter(m => visibleSections.includes(m.id));

  const languageLabels = {
    en: 'English', ko: '한국어', ja: '日本語', es: 'Español',
    fr: 'Français', id: 'Indonesia', pt: 'Português', ru: 'Русский',
    tr: 'Türkçe', de: 'Deutsch', zh: '简体中文', 'zh-TW': '繁體中文',
  };
  const langCodes = Object.keys(languageLabels);

  return (
    <header className="nav">
      {/* 로고 클릭 시 홈 최상단 또는 맨 위로 스크롤 */}
      <div className="logo" onClick={handleLogoClick}>
        TravelMate
      </div>

      {/* 모바일 햄버거 토글 */}
      <button
        className="nav-toggle"
        onClick={() => setIsOpen(o => !o)}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        {isOpen ? <X size={24}/> : <Menu size={24}/>}
      </button>

      {/* 메뉴 항목 */}
      <nav className={isOpen ? 'open' : ''}>
        {menus.map(m => (
          <button
            key={m.id}
            className={active === m.id ? 'active' : ''}
            onClick={() => goToSection(m.id)}
          >
            <span>{t(`navBar.menu.${m.id}`)}</span>
          </button>
        ))}
      </nav>

      {/* 언어 선택 */}
      <div className="nav-lang" ref={langRef}>
        <button className="lang-toggle" onClick={() => setLangOpen(o => !o)}>
          {languageLabels[lang]} ▾
        </button>
        {langOpen && (
          <ul className="lang-list">
            {langCodes.map(code => (
              <li key={code}>
                <button
                  className={`lang-item ${lang===code?'active':''}`}
                  onClick={() => { setLang(code); setLangOpen(false); }}
                >
                  {languageLabels[code]}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </header>
  );
}
